import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Trash2, FileText, Video, FileType2, ArrowLeft, Save, Upload, Check,
  PlayCircle, Mic, Loader2, Split, Package, UploadCloud, AlertTriangle,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { parseQuizFile } from "@/lib/quizParser";

const newModule = () => ({ id: crypto.randomUUID(), type: "text", title: "", text_content: "", video_url: "", pdf_path: "", duration_minutes: 0 });
const newQuestion = () => ({ id: crypto.randomUUID(), question: "", options: ["", "", "", ""], correct_index: 0 });

const ICONS = { video: Video, text: FileText, pdf: FileType2, scorm: Package };

export default function CourseEditorPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [course, setCourse] = useState({
    title: "",
    description: "",
    cpd_hours: 1,
    quiz_pass_pct: 80,
    cover_image_path: "",
    modules: [],
    quiz_questions: [],
  });
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("info");

  useEffect(() => {
    if (id) {
      api.get(`/courses/${id}`).then((r) => setCourse(r.data)).catch(() => toast.error("Course not found"));
    }
  }, [id]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: course.title,
        description: course.description,
        cpd_hours: Number(course.cpd_hours) || 0,
        quiz_pass_pct: Number(course.quiz_pass_pct) || 0,
        cover_image_path: course.cover_image_path || null,
        modules: course.modules,
        quiz_questions: course.quiz_questions,
      };
      let r;
      if (isNew) r = await api.post("/courses", payload);
      else r = await api.put(`/courses/${id}`, payload);
      toast.success("Course saved");
      if (isNew) navigate(`/admin/courses/${r.data.id}`);
      else setCourse(r.data);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.path;
  };

  const addModule = () => setCourse({ ...course, modules: [...course.modules, newModule()] });
  const updModule = (idx, patch) => {
    const m = [...course.modules];
    m[idx] = { ...m[idx], ...patch };
    setCourse({ ...course, modules: m });
  };
  const [scormDeleteTarget, setScormDeleteTarget] = useState(null); // {idx, m}

  const delModule = (idx) => {
    const m = course.modules[idx];
    if (m?.type === "scorm" && m?.scorm_package_id && !isNew) {
      // Route SCORM deletes through a confirm dialog because the on-disk
      // package (up to 500 MB) is removed and would need to be re-uploaded.
      setScormDeleteTarget({ idx, m });
      return;
    }
    const mods = [...course.modules];
    mods.splice(idx, 1);
    setCourse({ ...course, modules: mods });
  };
  const confirmScormDelete = async () => {
    if (!scormDeleteTarget) return;
    const { idx, m } = scormDeleteTarget;
    try {
      await api.delete(`/courses/${id}/scorm/${m.scorm_package_id}`);
      const mods = [...course.modules];
      mods.splice(idx, 1);
      setCourse({ ...course, modules: mods });
      setScormDeleteTarget(null);
      toast.success(`Removed "${m.title || "SCORM module"}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete SCORM package");
    }
  };
  const moveModule = (idx, dir) => {
    const m = [...course.modules];
    const j = idx + dir;
    if (j < 0 || j >= m.length) return;
    [m[idx], m[j]] = [m[j], m[idx]];
    setCourse({ ...course, modules: m });
  };

  const addQuestion = () => setCourse({ ...course, quiz_questions: [...course.quiz_questions, newQuestion()] });
  const updQuestion = (idx, patch) => {
    const q = [...course.quiz_questions];
    q[idx] = { ...q[idx], ...patch };
    setCourse({ ...course, quiz_questions: q });
  };
  const delQuestion = (idx) => {
    const q = [...course.quiz_questions];
    q.splice(idx, 1);
    setCourse({ ...course, quiz_questions: q });
  };
  const updOption = (qi, oi, val) => {
    const q = [...course.quiz_questions];
    const opts = [...q[qi].options];
    opts[oi] = val;
    q[qi] = { ...q[qi], options: opts };
    setCourse({ ...course, quiz_questions: q });
  };

  const importQuestions = (newQs, errors) => {
    if (!newQs.length) {
      toast.error(errors.length ? `Could not parse any questions. Example: ${errors[0]}` : "No questions found in file");
      return;
    }
    setCourse((c) => ({ ...c, quiz_questions: [...c.quiz_questions, ...newQs] }));
    if (errors.length) {
      toast.warning(`Imported ${newQs.length} question${newQs.length === 1 ? "" : "s"} · ${errors.length} skipped`);
    } else {
      toast.success(`Imported ${newQs.length} question${newQs.length === 1 ? "" : "s"}`);
    }
  };

  const launchPreview = async () => {
    if (isNew) {
      toast.error("Save the course first, then test it.");
      return;
    }
    try {
      const r = await api.post(`/courses/${id}/preview`);
      window.open(`/learn/${r.data.token}`, "_blank", "noopener");
      toast.success(r.data.reset ? "Preview reset — fresh test run opened" : "Preview opened in new tab");
    } catch {
      toast.error("Could not start preview");
    }
  };

  // ---------- SCORM upload ----------
  const [scormUpload, setScormUpload] = useState(null); // {progress, filename} | null

  const uploadScorm = useCallback(async (file) => {
    if (!/\.zip$/i.test(file.name)) {
      toast.error("Please drop a SCORM .zip file");
      return;
    }
    setScormUpload({ progress: 0, filename: file.name });

    // If the course hasn't been persisted yet, create it now so the SCORM
    // package can be attached. Use a filename-derived title as a fallback.
    let courseId = id;
    if (isNew) {
      try {
        const payload = {
          title: course.title || file.name.replace(/\.zip$/i, ""),
          description: course.description || "",
          cpd_hours: Number(course.cpd_hours) || 1,
          quiz_pass_pct: Number(course.quiz_pass_pct) || 80,
          cover_image_path: course.cover_image_path || null,
          modules: [],
          quiz_questions: [],
        };
        const cr = await api.post("/courses", payload);
        courseId = cr.data.id;
      } catch (e) {
        setScormUpload(null);
        toast.error("Could not create course before upload");
        return;
      }
    }

    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/courses/${courseId}/scorm/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setScormUpload({ progress: Math.round((evt.loaded * 100) / evt.total), filename: file.name });
          }
        },
      });
      const mod = r.data.module;
      if (isNew) {
        // Navigate to the persisted URL; useEffect will refetch fresh state incl. new module.
        navigate(`/admin/courses/${courseId}`, { replace: true });
      } else {
        setCourse((c) => ({ ...c, modules: [...c.modules, mod] }));
      }
      toast.success(`SCORM ${mod.scorm_version} package added — ${mod.title}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SCORM upload failed");
    } finally {
      setScormUpload(null);
    }
  }, [id, isNew, course.title, course.description, course.cpd_hours, course.quiz_pass_pct, course.cover_image_path, navigate]);

  // Per-module re-upload (replace in-place). Used by the "Package files missing"
  // warning so the admin can recover a broken module without creating a duplicate.
  const [scormReplacing, setScormReplacing] = useState({}); // {module_id: {progress, filename}}

  const replaceScorm = useCallback(async (moduleId, file) => {
    if (!/\.zip$/i.test(file.name)) {
      toast.error("Please drop a SCORM .zip file");
      return;
    }
    setScormReplacing((s) => ({ ...s, [moduleId]: { progress: 0, filename: file.name } }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/courses/${id}/scorm/${moduleId}/replace`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setScormReplacing((s) => ({
              ...s,
              [moduleId]: { progress: Math.round((evt.loaded * 100) / evt.total), filename: file.name },
            }));
          }
        },
      });
      const updated = r.data.module;
      setCourse((c) => ({
        ...c,
        modules: c.modules.map((m) => (m.id === moduleId ? updated : m)),
      }));
      toast.success(`SCORM package restored — ${updated.title}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SCORM re-upload failed");
    } finally {
      setScormReplacing((s) => {
        const n = { ...s };
        delete n[moduleId];
        return n;
      });
    }
  }, [id]);

  const [audioBusy, setAudioBusy] = useState({});
  const [bulkAudio, setBulkAudio] = useState(null);

  const generateAllAudio = async () => {
    if (isNew) {
      toast.error("Save the course first, then generate audio.");
      return;
    }
    const targets = (course.modules || []).filter(
      (m) => (m.type === "text" || m.type === "pdf") && !m.audio_path && (m.text_content || m.pdf_path)
    );
    if (targets.length === 0) {
      toast.info("Every eligible module already has audio.");
      return;
    }
    if (!window.confirm(`Generate professional audio for ${targets.length} module${targets.length === 1 ? "" : "s"}?\n\nThis uses your Emergent Universal Key budget.`)) return;

    setBulkAudio({ done: 0, total: targets.length, current: targets[0].title, failed: 0 });
    let failed = 0;
    for (let i = 0; i < targets.length; i++) {
      const m = targets[i];
      setBulkAudio((b) => ({ ...(b || {}), done: i, current: m.title, failed }));
      try {
        const r = await api.post(`/courses/${id}/modules/${m.id}/audio`);
        const modIdx = course.modules.findIndex((x) => x.id === m.id);
        if (modIdx >= 0) {
          updModule(modIdx, {
            audio_path: r.data.audio_path,
            audio_voice: r.data.voice,
            audio_model: r.data.model,
          });
        }
      } catch (e) { failed += 1; }
    }
    setBulkAudio({ done: targets.length, total: targets.length, current: null, failed });
    try {
      const fresh = await api.get(`/courses/${id}`);
      setCourse(fresh.data);
    } catch { /* ignore */ }
    if (failed === 0) toast.success(`Audio ready for all ${targets.length} module(s)`);
    else toast.warning(`Done — ${targets.length - failed} succeeded, ${failed} failed`);
    setTimeout(() => setBulkAudio(null), 3000);
  };
  const generateAudio = async (mod) => {
    if (isNew) { toast.error("Save the course first"); return; }
    setAudioBusy((b) => ({ ...b, [mod.id]: true }));
    try {
      const r = await api.post(`/courses/${id}/modules/${mod.id}/audio`);
      updModule(course.modules.findIndex((m) => m.id === mod.id), {
        audio_path: r.data.audio_path,
        audio_voice: r.data.voice,
        audio_model: r.data.model,
      });
      toast.success(`Audio ready — ${r.data.voice}, ${Math.round(r.data.size_bytes/1024)} KB`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audio generation failed");
    } finally {
      setAudioBusy((b) => ({ ...b, [mod.id]: false }));
    }
  };
  const clearAudio = async (mod) => {
    if (isNew) return;
    try {
      await api.delete(`/courses/${id}/modules/${mod.id}/audio`);
      updModule(course.modules.findIndex((m) => m.id === mod.id), {
        audio_path: null, audio_voice: null, audio_model: null,
      });
      toast.success("Audio removed");
    } catch { toast.error("Could not remove audio"); }
  };

  const [splitBusy, setSplitBusy] = useState({});
  const splitPdfModule = async (mod) => {
    if (isNew) { toast.error("Save the course first, then split"); return; }
    setSplitBusy((b) => ({ ...b, [mod.id]: true }));
    const tid = toast.loading("Splitting PDF into per-page modules…");
    try {
      const r = await api.post(`/courses/${id}/modules/${mod.id}/split-pdf`);
      setCourse(r.data.course);
      toast.success(`Split into ${r.data.pages_split} modules`, { id: tid });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not split PDF", { id: tid });
    } finally {
      setSplitBusy((b) => ({ ...b, [mod.id]: false }));
    }
  };

  return (
    <div className="p-6 md:p-12 max-w-6xl">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/courses" className="font-mono text-xs tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
          <ArrowLeft size={12} /> COURSES
        </Link>
      </div>
      <header className="mb-10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-3">/ {isNew ? "New" : "Edit"}</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">{course.title || "Untitled course"}</h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <>
              <Button
                onClick={generateAllAudio}
                disabled={bulkAudio !== null}
                data-testid="generate-all-audio-button"
                variant="outline"
                className="rounded-sm h-11"
                title="Generate professional audio narration for every text/PDF module that doesn't already have it"
              >
                <Mic size={16} strokeWidth={1.5} className="mr-2" /> Audio for all
              </Button>
              <Button
                onClick={launchPreview}
                data-testid="preview-course-button"
                variant="outline"
                className="rounded-sm h-11"
                title="Open this course as a candidate would see it. Test mode — does not affect real candidate stats."
              >
                <PlayCircle size={16} strokeWidth={1.5} className="mr-2" /> Test as candidate
              </Button>
            </>
          )}
          <Button onClick={save} disabled={busy} data-testid="save-course-button" className="rounded-sm h-11 bg-foreground text-background hover:bg-foreground/90">
            <Save size={16} strokeWidth={1.5} className="mr-2" /> {busy ? "Saving..." : "Save course"}
          </Button>
        </div>
      </header>

      <div className="border-b border-border mb-8 flex gap-6">
        {[
          { k: "info", label: "01 Info" },
          { k: "modules", label: `02 Modules (${course.modules.length})` },
          { k: "quiz", label: `03 Quiz (${course.quiz_questions.length})` },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`pb-3 font-mono text-xs tracking-[0.2em] uppercase border-b-2 -mb-px transition-colors ${
              tab === t.k ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "info" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-5">
            <div>
              <Label className="text-xs font-bold tracking-[0.2em] uppercase">Title</Label>
              <Input data-testid="course-title-input" value={course.title} onChange={(e) => setCourse({ ...course, title: e.target.value })} className="mt-2 rounded-sm h-11" />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-[0.2em] uppercase">Description</Label>
              <Textarea data-testid="course-description-input" rows={6} value={course.description} onChange={(e) => setCourse({ ...course, description: e.target.value })} className="mt-2 rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">CPD hours</Label>
                <Input data-testid="course-cpd-hours-input" type="number" step="0.25" value={course.cpd_hours} onChange={(e) => setCourse({ ...course, cpd_hours: e.target.value })} className="mt-2 rounded-sm h-11" />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.2em] uppercase">Quiz pass %</Label>
                <Input data-testid="course-pass-pct-input" type="number" min={0} max={100} value={course.quiz_pass_pct} onChange={(e) => setCourse({ ...course, quiz_pass_pct: e.target.value })} className="mt-2 rounded-sm h-11" />
              </div>
            </div>
          </div>
          <div className="border border-border p-6">
            <div className="eyebrow mb-3">/ Cover image</div>
            {course.cover_image_path ? (
              <div className="aspect-video bg-secondary border border-border overflow-hidden">
                <img src={fileUrl(course.cover_image_path)} alt="" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="aspect-video bg-secondary border border-dashed border-border flex items-center justify-center text-muted-foreground text-sm">
                No cover
              </div>
            )}
            <label className="mt-4 inline-flex items-center gap-2 border border-border px-4 py-2 text-sm cursor-pointer hover:bg-secondary transition-colors">
              <Upload size={14} strokeWidth={1.5} /> Upload cover
              <input
                type="file"
                accept="image/*"
                data-testid="course-cover-upload"
                className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try {
                    const path = await uploadFile(f);
                    setCourse({ ...course, cover_image_path: path });
                    toast.success("Cover uploaded");
                  } catch { toast.error("Upload failed"); }
                }}
              />
            </label>
          </div>
        </div>
      )}

      {tab === "modules" && (
        <div className="space-y-4">
          <ScormDropzone onFile={uploadScorm} uploading={scormUpload} isNew={isNew} />

          {course.modules.map((m, idx) => {
            const Icon = ICONS[m.type] || FileText;
            if (m.type === "scorm") {
              return (
                <div
                  key={m.id}
                  data-testid={`module-row-${idx}`}
                  className="border-2 border-foreground p-6 bg-secondary/40"
                >
                  <div className="flex items-start gap-5">
                    {/* Left rail: index + big SCORM package icon */}
                    <div className="flex flex-col items-center gap-3 shrink-0">
                      <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
                        /{String(idx + 1).padStart(2, "0")}
                      </span>
                      <div className="h-14 w-14 border-2 border-foreground bg-background flex items-center justify-center">
                        <Package size={22} strokeWidth={1.5} />
                      </div>
                      <span
                        className="font-mono text-[10px] tracking-widest uppercase px-2 py-1 bg-foreground text-background whitespace-nowrap"
                        data-testid={`scorm-version-${idx}`}
                      >
                        SCORM {m.scorm_version}
                      </span>
                    </div>

                    {/* Right: title + meta grid + actions */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-4 flex-wrap">
                        <div className="flex-1 min-w-[220px]">
                          <Label className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">
                            Module title
                          </Label>
                          <Input
                            data-testid={`module-title-${idx}`}
                            placeholder="Untitled SCORM module"
                            value={m.title}
                            onChange={(e) => updModule(idx, { title: e.target.value })}
                            className="mt-1 rounded-sm h-11 text-base font-semibold border-foreground/20 focus-visible:border-foreground"
                          />
                        </div>
                        <div className="flex items-center gap-1 pt-6">
                          <Button variant="ghost" size="sm" onClick={() => moveModule(idx, -1)} title="Move up">↑</Button>
                          <Button variant="ghost" size="sm" onClick={() => moveModule(idx, 1)} title="Move down">↓</Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => delModule(idx)}
                            data-testid={`delete-module-${idx}`}
                            title="Remove SCORM package"
                          >
                            <Trash2 size={14} className="text-destructive" />
                          </Button>
                        </div>
                      </div>

                      {m.package_missing && (
                        <div
                          data-testid={`scorm-missing-warning-${idx}`}
                          className="mb-4 border-2 border-destructive/40 bg-destructive/5 p-3 flex items-start gap-2 text-xs"
                        >
                          <AlertTriangle size={14} strokeWidth={1.5} className="text-destructive shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="font-semibold text-destructive">Package files missing on server</div>
                            <div className="mt-1 text-muted-foreground">
                              Re-upload the original .zip below to restore this module. Candidate progress is preserved and future redeploys will auto-recover automatically.
                            </div>
                            <div className="mt-3">
                              {scormReplacing[m.id] ? (
                                <div
                                  data-testid={`scorm-reupload-progress-${idx}`}
                                  className="font-mono text-[10px] text-muted-foreground"
                                >
                                  Uploading {scormReplacing[m.id].filename}… {scormReplacing[m.id].progress}%
                                </div>
                              ) : (
                                <label
                                  htmlFor={`scorm-reupload-${idx}`}
                                  className="inline-flex items-center gap-2 border border-destructive/60 bg-background hover:bg-destructive hover:text-destructive-foreground px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider cursor-pointer transition-colors"
                                  data-testid={`scorm-reupload-button-${idx}`}
                                >
                                  <Upload size={12} strokeWidth={1.5} />
                                  Re-upload original .zip
                                </label>
                              )}
                              <input
                                id={`scorm-reupload-${idx}`}
                                type="file"
                                accept=".zip,application/zip"
                                className="hidden"
                                data-testid={`scorm-reupload-input-${idx}`}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) replaceScorm(m.id, f);
                                  e.target.value = "";
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        <MetaBox label="Status" value={
                          m.package_missing ? (
                            <span className="inline-flex items-center gap-1.5 text-destructive font-semibold">
                              <AlertTriangle size={12} strokeWidth={2} />
                              Missing on server
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-foreground">
                              <Check size={12} className="text-accent" strokeWidth={2.5} />
                              Extracted
                            </span>
                          )
                        } />
                        <MetaBox label="Launch file" value={
                          <span className="font-mono truncate block" title={m.scorm_entry}>
                            {m.scorm_entry}
                          </span>
                        } />
                        <MetaBox label="Package size" value={
                          <span className="font-mono">
                            {formatBytes(m.scorm_size_bytes || 0)}
                          </span>
                        } />
                      </div>

                      <div className="pt-3 border-t border-border flex items-center justify-between gap-3 flex-wrap">
                        <div className="font-mono text-[10px] text-muted-foreground truncate min-w-0" title={m.scorm_original_filename}>
                          Uploaded from <span className="text-foreground">{m.scorm_original_filename}</span>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={launchPreview}
                          data-testid={`scorm-launch-${idx}`}
                          className="rounded-sm h-8 text-xs"
                          disabled={isNew}
                          title="Open this course as a candidate to test the SCORM package"
                        >
                          <PlayCircle size={12} strokeWidth={1.5} className="mr-1.5" />
                          Test SCORM
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={m.id} data-testid={`module-row-${idx}`} className="border border-border p-5">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-xs text-muted-foreground w-10">/{String(idx + 1).padStart(2, "0")}</span>
                  <Icon size={16} strokeWidth={1.5} />
                  <Select value={m.type} onValueChange={(v) => updModule(idx, { type: v })}>
                    <SelectTrigger data-testid={`module-type-${idx}`} className="w-40 rounded-sm h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="text">Text / Reading</SelectItem>
                      <SelectItem value="pdf">Slides / PDF Document</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input data-testid={`module-title-${idx}`} placeholder="Module title" value={m.title} onChange={(e) => updModule(idx, { title: e.target.value })} className="flex-1 rounded-sm h-9" />
                  <Button variant="ghost" size="sm" onClick={() => moveModule(idx, -1)}>↑</Button>
                  <Button variant="ghost" size="sm" onClick={() => moveModule(idx, 1)}>↓</Button>
                  <Button variant="ghost" size="sm" onClick={() => delModule(idx)} data-testid={`delete-module-${idx}`}>
                    <Trash2 size={14} className="text-destructive" />
                  </Button>
                </div>
                {m.type === "video" && (
                  <div>
                    <Label className="text-xs font-bold tracking-[0.2em] uppercase">Video URL (YouTube embed or MP4)</Label>
                    <Input data-testid={`module-video-url-${idx}`} value={m.video_url || ""} onChange={(e) => updModule(idx, { video_url: e.target.value })} className="mt-2 rounded-sm" placeholder="https://www.youtube.com/embed/..." />
                  </div>
                )}
                {m.type === "text" && (
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs font-bold tracking-[0.2em] uppercase">Text content</Label>
                      <Textarea data-testid={`module-text-${idx}`} rows={6} value={m.text_content || ""} onChange={(e) => updModule(idx, { text_content: e.target.value })} className="mt-2 rounded-sm font-mono text-sm" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold tracking-[0.2em] uppercase">Inline image (optional)</Label>
                      <div className="mt-2 flex items-center gap-3">
                        {m.image_path ? (
                          <>
                            <img src={fileUrl(m.image_path)} alt="" className="h-16 w-24 object-cover border border-border" />
                            <button
                              type="button"
                              data-testid={`module-image-clear-${idx}`}
                              onClick={() => updModule(idx, { image_path: null, image_caption: null })}
                              className="font-mono text-xs text-destructive hover:underline"
                            >Remove image</button>
                          </>
                        ) : (
                          <label className="inline-flex items-center gap-2 border border-border px-3 py-2 text-xs cursor-pointer hover:bg-secondary transition-colors">
                            <Upload size={12} strokeWidth={1.5} /> Add image
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              data-testid={`module-image-upload-${idx}`}
                              onChange={async (e) => {
                                const f = e.target.files?.[0];
                                if (!f) return;
                                try {
                                  const path = await uploadFile(f);
                                  updModule(idx, { image_path: path });
                                  toast.success("Image added");
                                } catch { toast.error("Upload failed"); }
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {m.type === "pdf" && (
                  <div>
                    <Label className="text-xs font-bold tracking-[0.2em] uppercase">Slides or PDF document</Label>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Accepts PDF or PowerPoint (.pptx, .ppt, .odp, .key). PowerPoint files are auto-converted to PDF on upload.
                    </div>
                    {m.pdf_path && (
                      <div className="mt-2 font-mono text-xs text-muted-foreground break-all flex items-center gap-2">
                        <Check size={12} className="text-accent" /> {m.pdf_path}
                      </div>
                    )}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <label className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm cursor-pointer hover:bg-secondary transition-colors">
                        <Upload size={14} strokeWidth={1.5} /> Upload slides or PDF
                        <input
                          type="file"
                          accept="application/pdf,.pdf,.pptx,.ppt,.odp,.key"
                          className="hidden"
                          data-testid={`module-pdf-upload-${idx}`}
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const isOffice = /\.(pptx|ppt|odp|key)$/i.test(f.name);
                            const tid = toast.loading(isOffice ? "Converting slides to PDF…" : "Uploading…");
                            try {
                              const path = await uploadFile(f);
                              updModule(idx, { pdf_path: path });
                              toast.success(isOffice ? "Slides converted & uploaded" : "PDF uploaded", { id: tid });
                            } catch { toast.error("Upload failed", { id: tid }); }
                          }}
                        />
                      </label>
                      {m.pdf_path && !isNew && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => splitPdfModule(m)}
                          disabled={splitBusy[m.id]}
                          data-testid={`split-pdf-${idx}`}
                          className="rounded-sm h-9 text-xs"
                        >
                          {splitBusy[m.id]
                            ? <><Loader2 size={12} className="animate-spin mr-1" /> Splitting…</>
                            : <><Split size={12} strokeWidth={1.5} className="mr-1" /> Split into per-page modules</>}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                {(m.type === "text" || m.type === "pdf") && !isNew && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
                        / Professional audio
                      </div>
                      {m.audio_path ? (
                        <>
                          <span className="inline-flex items-center gap-2 px-2 py-1 text-xs bg-foreground text-background font-mono">
                            <Check size={12} strokeWidth={2} /> Ready · {m.audio_voice || "nova"}
                          </span>
                          <audio data-testid={`module-audio-preview-${idx}`} src={fileUrl(m.audio_path)} controls className="h-8" style={{ maxWidth: 240 }} />
                          <Button size="sm" variant="outline" onClick={() => generateAudio(m)} disabled={audioBusy[m.id]} data-testid={`regen-audio-${idx}`} className="rounded-sm h-8 text-xs">
                            {audioBusy[m.id] ? <Loader2 size={12} className="animate-spin mr-1" /> : <Mic size={12} className="mr-1" />}
                            Regenerate
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => clearAudio(m)} data-testid={`clear-audio-${idx}`} className="rounded-sm h-8 text-xs hover:bg-destructive hover:text-destructive-foreground hover:border-destructive">
                            Remove
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" onClick={() => generateAudio(m)} disabled={audioBusy[m.id]} data-testid={`generate-audio-${idx}`} className="rounded-sm h-8 text-xs bg-foreground text-background">
                          {audioBusy[m.id] ? <><Loader2 size={12} className="animate-spin mr-1" /> Generating&hellip;</> : <><Mic size={12} className="mr-1" /> Generate professional audio</>}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <Button onClick={addModule} data-testid="add-module-button" variant="outline" className="w-full rounded-sm h-12 border-dashed">
            <Plus size={16} strokeWidth={1.5} className="mr-2" /> Add manual module
          </Button>
        </div>
      )}

      {tab === "quiz" && (
        <div className="space-y-6">
          <QuizDropzone onQuestions={importQuestions} />
          {course.quiz_questions.map((q, qi) => (
            <div key={q.id} data-testid={`question-row-${qi}`} className="border border-border p-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="font-mono text-xs text-muted-foreground w-10 pt-2">Q{String(qi + 1).padStart(2, "0")}</span>
                <Textarea data-testid={`question-text-${qi}`} rows={2} placeholder="Question" value={q.question} onChange={(e) => updQuestion(qi, { question: e.target.value })} className="flex-1 rounded-sm" />
                <Button variant="ghost" size="sm" onClick={() => delQuestion(qi)} data-testid={`delete-question-${qi}`}>
                  <Trash2 size={14} className="text-destructive" />
                </Button>
              </div>
              <div className="ml-13 grid grid-cols-1 md:grid-cols-2 gap-3">
                {q.options.map((o, oi) => (
                  <label key={oi} className={`flex items-center gap-3 border p-3 rounded-sm cursor-pointer transition-colors ${q.correct_index === oi ? "border-accent bg-accent/5" : "border-border hover:bg-secondary"}`}>
                    <input
                      type="radio"
                      name={`correct-${qi}`}
                      data-testid={`correct-${qi}-${oi}`}
                      checked={q.correct_index === oi}
                      onChange={() => updQuestion(qi, { correct_index: oi })}
                    />
                    <span className="font-mono text-xs text-muted-foreground">{String.fromCharCode(65 + oi)}</span>
                    <Input value={o} data-testid={`option-${qi}-${oi}`} onChange={(e) => updOption(qi, oi, e.target.value)} className="flex-1 rounded-sm h-9 border-0 shadow-none focus-visible:ring-0 px-0" placeholder="Option..." />
                  </label>
                ))}
              </div>
            </div>
          ))}
          <Button onClick={addQuestion} data-testid="add-question-button" variant="outline" className="w-full rounded-sm h-12 border-dashed">
            <Plus size={16} strokeWidth={1.5} className="mr-2" /> Add question
          </Button>
        </div>
      )}

      <AlertDialog open={!!scormDeleteTarget} onOpenChange={(open) => !open && setScormDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="scorm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-black tracking-tighter flex items-center gap-2">
              <AlertTriangle className="text-destructive" size={20} />
              Remove SCORM module?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the extracted package for
              &nbsp;<span className="font-semibold text-foreground">&ldquo;{scormDeleteTarget?.m?.title || "Untitled"}&rdquo;</span>
              &nbsp;from disk. You&apos;ll need to re-upload the .zip if you want it back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="scorm-delete-cancel" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmScormDelete}
              data-testid="scorm-delete-confirm"
              className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {bulkAudio && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="bulk-audio-overlay">
          <div className="bg-background border border-border rounded-sm w-full max-w-md p-6">
            <div className="eyebrow mb-3">/ Generating audio</div>
            <h2 className="text-2xl font-black tracking-tighter mb-4">
              {bulkAudio.done < bulkAudio.total ? `Module ${bulkAudio.done + 1} of ${bulkAudio.total}` : "Complete"}
            </h2>
            <div className="h-2 bg-secondary rounded-sm overflow-hidden">
              <div data-testid="bulk-audio-progress" className="h-full bg-foreground transition-all" style={{ width: `${Math.round((bulkAudio.done / bulkAudio.total) * 100)}%` }} />
            </div>
            <div className="mt-4 flex justify-between items-center text-xs font-mono text-muted-foreground">
              <span>{Math.round((bulkAudio.done / bulkAudio.total) * 100)}% done</span>
              {bulkAudio.failed > 0 && <span className="text-destructive">{bulkAudio.failed} failed</span>}
            </div>
            {bulkAudio.current && bulkAudio.done < bulkAudio.total && (
              <div className="mt-4 text-sm text-muted-foreground truncate" data-testid="bulk-audio-current">
                Now: <span className="text-foreground">{bulkAudio.current}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MetaBox({ label, value }) {
  return (
    <div className="border border-border bg-background px-3 py-2 min-w-0">
      <div className="font-mono text-[9px] tracking-widest uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 text-xs text-foreground truncate">{value}</div>
    </div>
  );
}

function formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function QuizDropzone({ onQuestions }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    setBusy(true);
    try {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      let text;
      if (ext === "docx" || ext === "pdf") {
        // Word / PDF → server-side text extraction, then parse client-side.
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/quiz/extract-text", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        text = r.data.text || "";
      } else {
        text = await file.text();
      }
      const { questions, errors } = parseQuizFile(file.name, text);
      onQuestions(questions, errors);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || String(e);
      onQuestions([], [detail]);
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div
      data-testid="quiz-dropzone"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      onClick={() => !busy && inputRef.current?.click()}
      className={`cursor-pointer border-2 border-dashed rounded-sm p-6 text-center transition-colors ${
        drag ? "border-foreground bg-secondary" : "border-border hover:border-foreground hover:bg-secondary/50"
      } ${busy ? "opacity-60 pointer-events-none" : ""}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".txt,.csv,.json,.md,.docx,.pdf,text/plain,application/json,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        data-testid="quiz-file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
      <UploadCloud size={28} strokeWidth={1.2} className="mx-auto text-muted-foreground" />
      <div className="mt-3 font-mono text-xs tracking-widest uppercase text-muted-foreground">/ Bulk import</div>
      <h3 className="mt-2 text-lg font-semibold tracking-tight">
        {busy ? "Reading file…" : "Drop a Word, PDF, .txt, .csv or .json quiz file"}
      </h3>
      <p className="mt-1 text-xs text-muted-foreground max-w-2xl mx-auto">
        Free-form text is auto-parsed. Example line:
        <br />
        <span className="font-mono">
          "Which colour is the sky? A Blue B Green C Red D Yellow (A is correct)"
        </span>
        <br />
        Word (.docx) and PDF files are read on the server so you can drop lesson handouts straight in.
      </p>
    </div>
  );
}

function ScormDropzone({ onFile, uploading, isNew }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  return (
    <div
      data-testid="scorm-dropzone"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      onClick={() => !uploading && inputRef.current?.click()}
      className={`cursor-pointer border-2 border-dashed rounded-sm p-8 text-center transition-colors ${
        drag ? "border-foreground bg-secondary" : "border-border hover:border-foreground hover:bg-secondary/50"
      } ${uploading ? "opacity-70 pointer-events-none" : ""}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".zip,application/zip,application/x-zip-compressed"
        className="hidden"
        data-testid="scorm-file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
      <UploadCloud size={32} strokeWidth={1.2} className="mx-auto text-muted-foreground" />
      <div className="mt-3 font-mono text-xs tracking-widest uppercase text-muted-foreground">/ SCORM package</div>
      <h3 className="mt-2 text-xl font-semibold tracking-tight">
        {uploading ? `Uploading ${uploading.filename}…` : "Drop a SCORM 1.2 or 2004 .zip here"}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {uploading
          ? `${uploading.progress}% — please don't close this tab`
          : isNew
            ? "Or click to browse. Dropping a file will save the course automatically and attach the SCORM as its first module."
            : "Or click to browse. Manifest is parsed automatically; the launch file becomes a new module."}
      </p>
      {uploading && (
        <div className="mt-4 h-2 bg-secondary rounded-sm overflow-hidden">
          <div className="h-full bg-foreground transition-all" style={{ width: `${uploading.progress}%` }} data-testid="scorm-upload-progress" />
        </div>
      )}
    </div>
  );
}
